import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

import Icon, { IconProps } from '../Icon'
import Modal from '../Modal'
import Text from '../Text'

import SuccessIcon from 'assets/sign-up-successful-check.svg'
import FunnelXIcon from 'assets/funnel-x.svg'
import { ButtonVariant } from 'components/Buttons/Button'
import { ThemeColor } from 'utils/colors'
import { TFunction } from 'i18next'
import { capitalizeFirstLetter } from 'utils/string'
import { BodyPortal } from 'components'

export const Actions = {
  info: 'info',
  success: 'success',
  warning: 'warning',
  discard: 'discard',
  delete: 'delete',
  deletePermanently: 'deletePermanently',
  deactivate: 'deactivate',
  reactivate: 'reactivate',
  save: 'save',
  change: 'change',
  approve: 'approve',
  reject: 'reject',
  markLost: 'markLost',
  markInvalid: 'markInvalid',
  update: 'update',
  sendEmail: 'sendEmail',
  publish: 'publish',
  jsonImport: 'jsonImport',
}

export type ActionVariant = keyof typeof Actions

export type Props = {
  submit?: () => Promise<void> | void
  cancel: () => void
  isSubmitting?: boolean
  title?: string
  children?: React.ReactNode | string
  icon?: Array<string> | string
  iconColor?: ThemeColor
  variant?: ActionVariant
  name?: string
  hideOk?: boolean
  okText?: string
  cancelText?: string
  okButtonVariant?: ButtonVariant
  autoClose?: boolean
  minWidth?: string
  minHeight?: string
  footer?: React.ReactNode
  isPortaled?: boolean
  disableOk?: boolean
}

const getDefaultContent = (t: TFunction, name: string, variant?: ActionVariant) => {
  const noun = name.toLocaleLowerCase()

  switch (variant) {
    case Actions.discard:
      return {
        title: t('confirm_title', { noun: t('changes'), action: t('discard_btn') }),
        body: t('confirm_discard_body'),
      }
    case Actions.delete:
      return {
        title: t('confirm_title', { noun: t(noun), action: t('delete_lbl') }),
        body: t('confirm_delete_body', { noun }),
      }
    case Actions.deletePermanently:
      return {
        title: t('confirm_title', { noun: t(noun), action: t('delete_lbl'), adverb: 'permanently' }),
        body: t('confirm_delete_permanently_body', { noun }),
      }
    case Actions.deactivate:
      return {
        title: t('confirm_title', { noun: t(noun), action: t('deactivate') }),
        body: t('confirm_deactivate_body', { noun }),
      }
    case Actions.reactivate:
      return {
        title: t('confirm_title', { noun: t(noun), action: t('reactivate') }),
        body: t('confirm_reactivate_body', { noun }),
      }
    case Actions.save:
      return {
        title: t('confirm_title', { noun: t('changes'), action: t('save_btn') }),
        body: t('confirm_save_body'),
      }
    case Actions.approve:
      return {
        title: t('confirm_title', { noun: t(noun), action: t('approve') }),
        body: t('confirm_approve_body', { noun }),
      }
    case Actions.reject:
      return {
        title: t('confirm_title', { noun: t(noun), action: t('reject') }),
        body: t('confirm_reject_body', { noun }),
      }
    case Actions.markLost:
      return {
        title: t('confirm_mark_title', {
          name: t('lead'),
          action: t('lost'),
        }),
        body: t('confirm_mark', {
          name: t('lead'),
          action: t('lost'),
        }),
      }
    case Actions.markInvalid:
      return {
        title: t('confirm_mark_title', {
          name: t('lead'),
          action: t('invalid'),
        }),
        body: t('confirm_mark', {
          name: t('lead'),
          action: t('invalid'),
        }),
      }
    case Actions.update:
      return {
        title: t('confirm_title', { noun: t(noun).toLowerCase(), action: t('update') }),
        body: t('confirm_body', { noun: t(noun).toLowerCase(), action: t('update') }),
      }
    case Actions.sendEmail:
      return {
        title: t('send_email_title'),
        body: t('confirm_body', {
          noun: t(noun).toLowerCase(),
          action: `${t('send_email_title').toLowerCase()} ${t('to_lbl').toLowerCase()}`,
        }),
      }
    default:
      return null
  }
}

const ConfirmationModal: React.FC<Props> = ({
  submit,
  cancel,
  children,
  title,
  icon,
  iconColor = 'blue1',
  variant,
  name = 'item',
  isSubmitting,
  hideOk = false,
  autoClose = true,
  okText,
  cancelText,
  okButtonVariant = 'fill',
  minWidth,
  minHeight,
  footer,
  isPortaled = false,
  disableOk = false,
}) => {
  const { t } = useTranslation()
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async () => {
    if (submit) {
      if (isSubmitting) {
        await submit()
        autoClose && cancel()
        return
      }

      setSubmitting(true)
      await submit()
      setSubmitting(false)
      autoClose && cancel()
    }
  }

  const getIcons = (variant: Exclude<Props['variant'], undefined>) => {
    const iconData = icons[variant] as IconProps
    if (iconData?.icon && iconData?.color) {
      return <Icon color={iconData?.color} fontSize={50} icon={iconData?.icon} mb="1rem" />
    }

    return icons[variant]
  }

  useEffect(() => {
    let abort = false

    if (!abort) {
      setSubmitting(Boolean(isSubmitting))
    }

    return () => {
      abort = true
    }
  }, [isSubmitting])

  const modalIcon = variant ? (
    getIcons(variant)
  ) : icon ? (
    <Icon color={iconColor} fontSize={50} icon={icon} mb={16} />
  ) : (
    <></>
  )
  const content = getDefaultContent(t, name, variant) ?? { title, body: children }

  const modal = (
    <Modal
      cancelText={cancelText || 'cancel_btn'}
      isSubmitting={isSubmitting || submitting}
      okText={okText || 'btn_confirm'}
      okButtonVariant={okButtonVariant}
      onClose={cancel}
      onOk={handleSubmit}
      hideOk={hideOk}
      minWidth={minWidth}
      minHeight={minHeight}
      visible
      footer={footer}
      disableOk={disableOk}
    >
      {modalIcon}
      <TextTitle textAlign="center" fontSize={16} fontWeight="bold" lineHeight="24px" mb="8px">
        {title ? title : capitalizeFirstLetter(content.title?.toLowerCase() || '')}
      </TextTitle>
      {!children && (
        <Text textAlign="center" fontSize={14} lineHeight="24px">
          {content.body}
        </Text>
      )}
      {children && typeof children === 'string' && (
        <Text fontSize={14} lineHeight="24px">
          {children}
        </Text>
      )}
      {children && typeof children !== 'string' && children}
    </Modal>
  )

  return isPortaled ? <BodyPortal>{modal}</BodyPortal> : <>{modal}</>
}

export default ConfirmationModal

const TextTitle = styled(Text)`
  ::first-letter {
    text-transform: uppercase;
  }
`

const SuccessIconWrapper = styled.div`
  > svg {
    transform: scale(0.75);

    > g {
      fill: ${(p) => p.theme.colors.green};
    }
  }
`

const IconWrapper = styled.div`
  margin-bottom: 1rem;
`

type CustomIconType = 'success' | 'markLost'
type IconCommon = { [key in Exclude<Props['variant'], undefined | CustomIconType>]: IconProps }
type IconCustom = { [key in CustomIconType]: JSX.Element }
export type ConfirmIcon = IconCommon & IconCustom

export const icons: ConfirmIcon = {
  info: { icon: ['fal', 'envelope-open-text'], color: 'orange' },
  warning: { icon: ['far', 'exclamation-triangle'], color: 'yellow' },
  discard: { icon: ['far', 'exclamation-triangle'], color: 'orange' },
  delete: { icon: ['fal', 'trash-alt'], color: 'red' },
  deletePermanently: { icon: ['far', 'trash-can-xmark'], color: 'red' },
  deactivate: { icon: ['fal', 'minus-octagon'], color: 'red' },
  reactivate: { icon: ['fal', 'repeat'], color: 'blue1' },
  save: { icon: ['far', 'pencil-alt'], color: 'blue1' },
  change: { icon: ['far', 'arrows-to-dotted-line'], color: 'blue1' },
  success: (
    <SuccessIconWrapper>
      <SuccessIcon />
    </SuccessIconWrapper>
  ),
  approve: { icon: ['fas', 'check-circle'], color: 'green' },
  reject: { icon: ['fas', 'times-circle'], color: 'red' },
  markLost: (
    <IconWrapper>
      <FunnelXIcon width="47" height="38" viewBox="0 0 18 12" />
    </IconWrapper>
  ),
  markInvalid: { icon: ['fal', 'phone-slash'], color: 'yellow1' },
  update: { icon: ['far', 'pencil-alt'], color: 'blue1' },
  sendEmail: { icon: ['far', 'envelope'], color: 'blue1' },
  publish: { icon: ['fas', 'arrow-down-to-dotted-line'], color: 'blue1' },
  jsonImport: { icon: ['fa', 'code'], color: 'blue1' },
}
